import {
  createConnectQueryKey,
  createQueryOptions,
  useMutation,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { usePrefetchQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getAssessorListQuestionnairesQueryKey } from '@/api/assessor-questionnaire.hook'
import {
  getGetCompanyActivityLogQueryKey,
  getGetCompanyDrawerMetadataQueryKey,
  getGetCompanyQueryKey,
  getListCompaniesQueryKey,
} from '@/api/company.hook'
import { getListControlsQueryKey } from '@/api/control.hook'
import { getListQuestionnairesQueryKey } from '@/api/optimistic-update-questionnaire.hook'
import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'
import {
  createProject,
  deleteAssessmentScoreLabels,
  deleteOngoingAssessment,
  deleteProject,
  finalizeAssessment,
  getAssessment,
  getAssessmentNotesYdoc,
  getOngoingAssessment,
  getProject,
  getRoomByAssessmentId,
  listAssessmentCadenceSettings,
  listAssessmentScoreLabels,
  listAssessments,
  listOngoingAssessments,
  listProjects,
  setAssessmentCadenceSetting,
  setAssessmentScoreLabels,
  startAssessment,
  submitAssessmentForApproval,
  updateAssessmentDueDate,
  updateProjectName,
} from '@/gen/inventory/v1/assessment_service-AssessmentService_connectquery'
import {
  AssessmentInfo,
  AssessmentScoreLabel,
  AssessmentService,
  GetRoomByAssessmentIdResponse,
} from '@/gen/inventory/v1/assessment_service_pb'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { Project } from '@/gen/inventory/v1/project_pb'

import { assessmentScoreLabel } from '@/const/label'

import { defaultTransport } from '@/lib/connect/connect-transport'

export const getListAssessmentCadenceSettingsQueryKey = () => {
  return createConnectQueryKey({
    schema: AssessmentService.method.listAssessmentCadenceSettings,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListAssessmentScoreLabelsQueryKey = () => {
  return createConnectQueryKey({
    schema: AssessmentService.method.listAssessmentScoreLabels,
    input: {},
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListAssessmentsQueryKey = (companyId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.listAssessments,
    input: { companyId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListOngoingAssessmentsQueryKey = (companyId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.listOngoingAssessments,
    input: { companyId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getGetOngoingAssessmentQueryKey = (ongoingAssessmentId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.getOngoingAssessment,
    input: { ongoingAssessmentId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getGetRoomByAssessmentIdQueryKey = (
  assessmentId?: GetRoomByAssessmentIdResponse['roomId'],
) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.getRoomByAssessmentId,
    input: { assessmentId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListProjectsQueryKey = (withDeleted = false) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.listProjects,
    input: { withDeleted },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getGetProjectQueryKey = (projectId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentService.method.getProject,
    input: { projectId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useListAssessmentCadenceSettings = () => {
  const queryState = useQuery(listAssessmentCadenceSettings)

  return {
    ...queryState,
    settings: queryState.data?.settings || [],
  }
}

export const toAssessmentScoreLabels = (
  labels: Pick<AssessmentScoreLabel, 'score' | 'label'>[],
): Record<AssessmentScore, string> =>
  labels.reduce((acc, { score, label }) => ({ ...acc, [score]: label }), assessmentScoreLabel)

export const useAssessmentScoreLabels = () => {
  const queryState = useQuery(listAssessmentScoreLabels)
  const labels = queryState.data?.labels
  const scoreLabels = useMemo(() => toAssessmentScoreLabels(labels ?? []), [labels])

  return { ...queryState, scoreLabels }
}

export const usePrefetchAssessmentScoreLabels = () => {
  const transport = useTransport()

  usePrefetchQuery(createQueryOptions(listAssessmentScoreLabels, {}, { transport }))
}

export const useSetAssessmentScoreLabels = () => {
  const queryClient = useQueryClient()

  return useMutation(setAssessmentScoreLabels, {
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: getListAssessmentScoreLabelsQueryKey() }),
  })
}

export const useDeleteAssessmentScoreLabels = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteAssessmentScoreLabels, {
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: getListAssessmentScoreLabelsQueryKey() }),
  })
}

export const useSetAssessmentCadence = () => {
  const queryClient = useQueryClient()

  return useMutation(setAssessmentCadenceSetting, {
    onSuccess: () => {
      queryClient.removeQueries({ type: 'inactive' })
      queryClient.invalidateQueries({ queryKey: getListAssessmentCadenceSettingsQueryKey() })
    },
  })
}

export const useSubmitAssessmentForApproval = (companyId?: Company['id']) => {
  const queryClient = useQueryClient()

  return useMutation(submitAssessmentForApproval, {
    onSuccess: (_, { ongoingAssessmentId }) => {
      // Status flipped to AWAITING_APPROVAL on the ongoing assessment, and a
      // report-generation message was produced. Invalidate the ongoing read
      // so the UI re-renders the new state, and the company / ongoing list
      // queries so any status-driven UI elsewhere refreshes.
      queryClient.invalidateQueries({
        queryKey: getGetOngoingAssessmentQueryKey(ongoingAssessmentId),
      })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      }
    },
  })
}

export const useFinalizeAssessment = () => {
  const queryClient = useQueryClient()

  return useMutation(finalizeAssessment, {
    onSuccess: (_, { companyId, assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: getListAssessmentsQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetCompanyActivityLogQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getGetRoomByAssessmentIdQueryKey(assessmentId) })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({
        queryKey: getAssessorListQuestionnairesQueryKey(companyId, assessmentId),
      })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}

export const useStartAssessment = (companyId?: Company['id']) => {
  const queryClient = useQueryClient()

  return useMutation(startAssessment, {
    onSuccess: (_, { companyId: startedCompanyId }) => {
      queryClient.invalidateQueries({
        queryKey: getListOngoingAssessmentsQueryKey(startedCompanyId || companyId),
      })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({
        queryKey: getListQuestionnairesQueryKey(startedCompanyId || companyId),
      })
      queryClient.invalidateQueries({
        queryKey: getGetCompanyQueryKey(startedCompanyId || companyId),
      })
      queryClient.invalidateQueries({
        queryKey: getGetCompanyActivityLogQueryKey(startedCompanyId || companyId),
      })
      queryClient.invalidateQueries({
        queryKey: getGetCompanyDrawerMetadataQueryKey(startedCompanyId || companyId),
      })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}

export const useListOngoingAssessments = (companyId?: Company['id'], enabled = true) => {
  const queryState = useQuery(
    listOngoingAssessments,
    { companyId: companyId || undefined },
    { enabled },
  )

  return {
    ...queryState,
    ongoingAssessments: queryState.data?.ongoingAssessments || [],
  }
}

export const useGetOngoingAssessment = (ongoingAssessmentId?: OngoingAssessment['id']) => {
  const queryState = useQuery(getOngoingAssessment, { ongoingAssessmentId })

  return {
    ...queryState,
    ongoingAssessment: queryState.data?.ongoingAssessment,
  }
}

export const useListAssessments = (companyId?: string) => {
  const queryState = useQuery(listAssessments, { companyId })

  return {
    ...queryState,
    assessments: queryState.data?.assessments || [],
  }
}

export const useGetAssessment = (assessmentId?: AssessmentInfo['id']) => {
  const queryState = useQuery(getAssessment, { assessmentId })

  return {
    ...queryState,
    assessment: queryState.data?.assessment,
  }
}

export const useGetAssessmentNotesYdoc = (assessmentId?: AssessmentInfo['id']) => {
  const queryState = useQuery(getAssessmentNotesYdoc, { assessmentId })

  return {
    ...queryState,
    notesYdoc: queryState.data?.notesYdoc,
  }
}

export const useGetRoomByAssessmentId = (assessmentId: AssessmentInfo['id']) => {
  const queryState = useQuery(getRoomByAssessmentId, { assessmentId })

  return {
    ...queryState,
    roomId: queryState.data?.roomId,
  }
}

export const useListProjects = (withDeleted = false) => {
  const queryState = useQuery(listProjects, { withDeleted })
  return {
    ...queryState,
    projects: queryState.data?.projects || [],
  }
}

export const useGetProject = (projectId?: Project['id']) => {
  const queryState = useQuery(getProject, { projectId }, { enabled: !!projectId })
  return {
    ...queryState,
    project: queryState.data?.project,
    controlScopes: queryState.data?.controlScopes,
    members: queryState.data?.project?.members,
  }
}

export const useCreateProject = () => {
  const queryClient = useQueryClient()

  return useMutation(createProject, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
    },
  })
}

export const useUpdateProjectName = () => {
  const queryClient = useQueryClient()

  return useMutation(updateProjectName, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey() })
    },
  })
}

export const useDeleteProject = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteProject, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey() })
    },
  })
}

export const useUpdateAssessmentDueDate = () => {
  const queryClient = useQueryClient()

  return useMutation(updateAssessmentDueDate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetOngoingAssessmentQueryKey() })
    },
  })
}

export const useDeleteOngoingAssessment = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteOngoingAssessment, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListOngoingAssessmentsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompaniesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}
